import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useMany } from "@refinedev/core";
import { DateField, EditButton, ShowButton, useDataGrid } from "@refinedev/mui";
import { format } from "date-fns";
import { debounce } from "lodash";
import { List } from "pages/in-stock/components/List";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getClients } from "services/client";
import { getFarmers } from "services/farmer";
import { ActionCreate, CustomerType } from "./create";

export const ActionList = () => {
  const [showType, setShowType] = useState<CustomerType>(CustomerType.CLIENT);
  const { dataGridProps, setFilters, tableQueryResult } = useDataGrid<any>({
    initialSorter: [
      {
        field: "customer_type",
        order: "desc",
      },
    ],
    filters: {},
    initialFilter: [
      {
        field: "customer_type",
        operator: "eq",
        value: showType,
      },
    ],
    initialPageSize: 5,
  });

  const [open, setOpen] = useState<boolean>(false);

  const [searchDate, setSearchDate] = useState("");

  const onClose = () => {
    setOpen(false);
  };

  const handleExportExcel = () => {
    // Implement export to Excel logic here
  };

  const handlePrintInvoice = () => {
    // Implement print invoice logic here
    window.print();
  };

  const { data: dataClients, isLoading: isLoadingClients } = useQuery({
    queryKey: "clients",
    queryFn: getClients(),
  });

  const { data: dataFarmers, isLoading: isLoadingFarmers } = useQuery({
    queryKey: "farmers",
    queryFn: getFarmers(),
  });

  const { data: containerData, isLoading: containerIsLoading } = useMany({
    resource: "containers",
    ids: dataGridProps?.rows?.map((item: any) => item?.container) ?? [],
    queryOptions: {
      enabled: !!dataGridProps?.rows,
    },
  });

  const columns = React.useMemo<GridColDef<any>[]>(
    () => [
      {
        field: "type",
        headerName: "Type",
      },
      {
        field: "quantity",
        headerName: "Quantity",
        type: "number",
      },
      {
        field: "date",
        headerName: "Date",
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
      },
      {
        field: "container",
        headerName: "Container",
        minWidth: 300,
        renderCell: function render({ value }) {
          const container = containerData?.data?.find((item) => item.id === value?.id);
          return containerIsLoading ? (
            <>Loading...</>
          ) : (
            `${
              container?.type ?? ""
            } - ${
              container?.name ?? ""
            }`
          );
        },
      },
      showType === CustomerType.CLIENT
        ? {
            field: "client",
            headerName: "Client",
            valueGetter: ({ row }) => {
              const value = row?.client?.name;
              return value;
            },
            minWidth: 300,
            renderCell: function render({ value }) {
              const client: any = (dataClients || []).find(
                (item: any) => item.name === value
              );
              return isLoadingClients ? <>Loading...</> : client?.name || "- -";
            },
          }
        : {
            field: "farmer",
            headerName: "Farmer",
            valueGetter: ({ row }) => {
              const value = row?.farmer?.name;
              return value;
            },
            minWidth: 300,
            renderCell: function render({ value }) {
              const farmer: any = (dataFarmers || []).find(
                (item: any) => item.name === value
              );
              return isLoadingFarmers ? (
                <>Loading...</>
              ) : (
                farmer?.name || "- -"
              );
            },
          },
      {
        field: "actions",
        headerName: "Actions",
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row?.id} />
              <ShowButton hideText recordItemId={row?.id} />
              <PrintIcon onClick={() => window.print()} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 80,
      },
    ],
    [containerData?.data, dataClients, showType]
  );

  useEffect(() => {
    const filters = [
      {
        field: "customer_type",
        value: CustomerType.CLIENT,
        operator: "contains",
      },
      ...(searchDate !== ""
        ? [
            {
              field: "container",
              value: searchDate,
              operator: "contains",
            },
          ]
        : []),
    ] as any;

    setFilters(filters);
  }, []);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const handleFromDateChange = (date: any) => {
    setFromDate(date);
  };

  const handleToDateChange = (date: any) => {
    setToDate(date);
  };

  const handleSearch = async () => {
    const filters = [] as any;

    if (fromDate) {
      filters.push({
        field: "date",
        value: format(fromDate, "yyyy-MM-dd"),
        operator: "gte",
      });
    }

    if (toDate) {
      filters.push({
        field: "date",
        value: format(toDate, "yyyy-MM-dd"),
        operator: "lte",
      });
    }
    setFilters(filters);
  };
  const setFiltersDebounced = debounce((value) => {
    setFilters(value);
  }, 500);

  // useEffect(() => {
  //   const filters =
  //     searchDate !== ""
  //       ? [{ field: "container", value: searchDate, operator: "contains" }]
  //       : [];

  //   setFiltersDebounced(filters);

  //   return () => {
  //     setFiltersDebounced.cancel();
  //   };
  // }, [searchDate, setFiltersDebounced]);

  const [next, setNext] = React.useState<string | undefined>(undefined);

  const { data } = tableQueryResult;

  const handlePaginationChange = (
    model: GridPaginationModel,
    details: GridCallbackDetails
  ) => {
    const lastRow = data?.data[data.data.length - 1];
    console.log({ lastRow });

    const next = lastRow?.createdAt;

    if (next) {
      setNext(next);
    }

    dataGridProps.onPaginationModelChange?.(model, details);
  };

  const {
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    ...restDataGridProps
  } = dataGridProps;

  return (
    <List open={open} setOpen={setOpen}>
      <div id="invoice-content" style={{ display: "none" }}>
        {/* Structure your invoice content here */}
        <Typography variant="h5">Invoice</Typography>
        {/* Include details like client name, date, quantity, etc. */}
      </div>
      <Stack direction="row" alignContent="end" justifyContent="end" mb={2}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Toolbar
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "start",
              columnGap: "1rem",
            }}
          >
            <TextField
              label="Search"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiInputBase-input": {
                  height: "1rem",
                },
              }}
            />
            <DatePicker
              label="From"
              value={fromDate}
              onChange={handleFromDateChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "3rem",
                },
                width: "15rem",
              }}
              slotProps={{
                textField: {
                  helperText: "MM/DD/YYYY",
                },
              }}
            />
            <DatePicker
              label="To"
              value={toDate}
              onChange={handleToDateChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "3rem",
                },
                width: "15rem",
              }}
              slotProps={{
                textField: {
                  helperText: "MM/DD/YYYY",
                },
              }}
            />
            <Button onClick={handleSearch}>Search</Button>
            <IconButton onClick={handleExportExcel}>
              <GetAppIcon />
            </IconButton>
            <IconButton onClick={handlePrintInvoice}>
              <PrintIcon />
            </IconButton>
          </Toolbar>
        </LocalizationProvider>
      </Stack>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Create Client</DialogTitle>
        <DialogContent>
          <ActionCreate onClose={onClose} />
        </DialogContent>
      </Dialog>
      <RadioGroup
        value={showType}
        onChange={(event, value: any) => {
          setShowType(value);
          setFilters([{ field: "customer_type", value, operator: "contains" }]);
        }}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: 4,
        }}
      >
        <FormControl>
          <FormLabel>
            Client
            <Radio value={CustomerType.CLIENT} />
          </FormLabel>
        </FormControl>
        {/* <FormLabel> */}
        <FormControl>
          <FormLabel>
            Farmer
            <Radio value={CustomerType.FARMER} />
          </FormLabel>
        </FormControl>
        {/* </FormControl> */}
      </RadioGroup>
      <DataGrid
        pagination
        {...restDataGridProps}
        pageSizeOptions={[5, 10, 25, 100]}
        paginationMode="server"
        columns={columns}
        onPaginationModelChange={handlePaginationChange}
        autoHeight
      />
    </List>
  );
};
